import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  User, Calendar, BookOpen, Award, ArrowLeft, Edit, Save, X, 
  Clock, CheckCircle, Star, Download, Info, AlertCircle, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import StarRating from './StarRating';
import ViewAsUser from './ViewAsUser';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserProfile = ({ userId, onClose, isOwnProfile = false }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: '',
    surname: '',
    email: '',
    roles: [],
    approved: false
  });
  
  const [athleteData, setAthleteData] = useState({
    birth_date: '',
    fiscal_code: '',
    athlete_class: '',
    asa_reference: '',
    maestro_id: '',
    phone: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    region: '',
    notes: ''
  });
  
  const [isEditingAthleteProfile, setIsEditingAthleteProfile] = useState(false);
  
  // Credits summary state
  const [creditsSummary, setCreditsSummary] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [technicalCreditsHistory, setTechnicalCreditsHistory] = useState([]);
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);
  
  // Maestro list for dropdown
  const [maestros, setMaestros] = useState([]);
  const [loadingMaestros, setLoadingMaestros] = useState(false);
  
  // View as user state
  const [showViewAsUser, setShowViewAsUser] = useState(false);

  // Helper function to get primary role for display
  const getPrimaryRole = (user) => {
    if (user.roles && user.roles.length > 0) {
      return user.roles[0];
    }
    return user.role || 'atleta';
  };

  // Helper function to get all roles for display
  const getAllRoles = (user) => {
    if (user.roles && user.roles.length > 0) {
      return user.roles;
    }
    return user.role ? [user.role] : ['atleta'];
  };

  // Check if user is an athlete
  const isAthlete = (user) => {
    const userRoles = getAllRoles(user);
    return userRoles.includes('atleta');
  };

  const isMaestro = (user) => {
    const userRoles = getAllRoles(user);
    return userRoles.includes('maestro');
  };


  // Validate fiscal code format
  const validateFiscalCode = (code) => {
    if (!code || typeof code !== 'string') return false;
    const regex = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/;
    return regex.test(code.toUpperCase());
  };

  // Validate birth date format
  const validateBirthDate = (date) => {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!regex.test(date)) return false;
    
    const [day, month, year] = date.split('/').map(Number);
    return day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2020;
  };

  // Validate postal code (5 digits)
  const validatePostalCode = (code) => {
    if (!code) return true; // Optional field
    return /^\d{5}$/.test(code);
  };

  const updateAthleteProfile = async () => {
    try {
      // Validate data before sending
      if (athleteData.birth_date && !validateBirthDate(athleteData.birth_date)) {
        toast.error('Formato data nascita non valido. Usa DD/MM/YYYY');
        return;
      }
      
      if (athleteData.fiscal_code && !validateFiscalCode(athleteData.fiscal_code)) {
        toast.error('Formato codice fiscale non valido');
        return;
      }

      if (athleteData.postal_code && !validatePostalCode(athleteData.postal_code)) {
        toast.error('CAP non valido. Deve contenere 5 cifre');
        return;
      }

      // Prepare data, ensuring fiscal code is uppercase and only send non-empty values
      const updateData = {};
      
      if (athleteData.birth_date !== undefined) {
        updateData.birth_date = athleteData.birth_date?.trim() || null;
      }
      
      if (athleteData.fiscal_code !== undefined) {
        updateData.fiscal_code = athleteData.fiscal_code ? athleteData.fiscal_code.toUpperCase().trim() : null;
      }
      
      if (athleteData.athlete_class !== undefined) {
        updateData.athlete_class = athleteData.athlete_class || null;
      }
      
      if (athleteData.asa_reference !== undefined) {
        updateData.asa_reference = athleteData.asa_reference?.trim() || null;
      }

      // Contact and address fields - editable by all
      if (athleteData.phone !== undefined) {
        updateData.phone = athleteData.phone?.trim() || null;
      }
      if (athleteData.address !== undefined) {
        updateData.address = athleteData.address?.trim() || null;
      }
      if (athleteData.city !== undefined) {
        updateData.city = athleteData.city?.trim() || null;
      }
      if (athleteData.province !== undefined) {
        updateData.province = athleteData.province?.toUpperCase().trim() || null;
      }
      if (athleteData.postal_code !== undefined) {
        updateData.postal_code = athleteData.postal_code?.trim() || null;
      }
      if (athleteData.region !== undefined) {
        updateData.region = athleteData.region || null;
      }
      if (athleteData.maestro_id !== undefined) {
        updateData.maestro_id = athleteData.maestro_id || null;
      }

      // Admin-only fields
      if (!isOwnProfile) {
        if (athleteData.notes !== undefined) {
          updateData.notes = athleteData.notes?.trim() || null;
        }
      }
      
      // Check if there's any data to update
      if (Object.keys(updateData).length === 0) {
        toast.error('Inserisci almeno un dato da aggiornare');
        return;
      }

      const token = localStorage.getItem('token');
      
      // Use different endpoint based on whether admin is editing or user is editing own profile
      const endpoint = isOwnProfile 
        ? `${API}/users/profile/athlete` 
        : `${API}/admin/users/${userId}/athlete-profile`;
      
      await axios.patch(endpoint, updateData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      toast.success('Profilo atleta aggiornato con successo');
      setIsEditingAthleteProfile(false);
      fetchUserProfile(); // Refresh data
    } catch (error) {
      console.error('Error updating athlete profile:', error);
      
      // Handle different error formats
      let message = 'Errore nell\'aggiornamento del profilo atleta';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array (Pydantic validation errors)
        if (Array.isArray(detail)) {
          message = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          message = detail;
        }
        // If detail is an object
        else if (typeof detail === 'object') {
          message = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(message);
    }
  };

  useEffect(() => {
    if (userId) {
      setLoading(true);
      setProfile(null);
      const loadProfile = async (retryCount = 0) => {
        try {
          const endpoint = isOwnProfile 
            ? `${API}/users/me/profile` 
            : `${API}/admin/users/${userId}/profile`;
          
          const response = await axios.get(endpoint, { timeout: 15000 });
          
          setProfile(response.data);
          setEditData({
            name: response.data.user.name,
            surname: response.data.user.surname,
            email: response.data.user.email || '',
            roles: getAllRoles(response.data.user),
            approved: response.data.user.approved
          });

          if (isAthlete(response.data.user)) {
            setAthleteData({
              birth_date: response.data.user.birth_date || '',
              fiscal_code: response.data.user.fiscal_code || '',
              athlete_class: response.data.user.athlete_class || '',
              asa_reference: response.data.user.asa_reference || '',
              maestro_id: response.data.user.maestro_id || '',
              phone: response.data.user.phone || '',
              address: response.data.user.address || '',
              city: response.data.user.city || '',
              province: response.data.user.province || '',
              postal_code: response.data.user.postal_code || '',
              region: response.data.user.region || '',
              notes: response.data.user.notes || ''
            });
            fetchCreditsSummary(response.data.user.id);
            fetchMaestros();
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          if (retryCount < 2 && (error.code === 'ECONNABORTED' || !error.response)) {
            setTimeout(() => loadProfile(retryCount + 1), 1000 * (retryCount + 1));
            return;
          }
          if (error.response?.status === 404) {
            toast.error('Profilo utente non trovato');
          } else if (error.response?.status === 401) {
            // Do nothing - the interceptor handles token expiration
          } else if (error.response?.status === 403) {
            toast.error('Non hai i permessi per visualizzare questo profilo');
          } else {
            toast.error('Errore nel caricamento del profilo. Riprova tra qualche secondo.');
          }
        } finally {
          setLoading(false);
        }
      };
      
      loadProfile();
    }
  }, [userId, isOwnProfile]);

  const fetchUserProfile = async () => {
    try {
      // Use different endpoint based on whether viewing own profile or admin viewing another user's profile
      const endpoint = isOwnProfile 
        ? `${API}/users/me/profile` 
        : `${API}/admin/users/${userId}/profile`;
      
      const response = await axios.get(endpoint);
      
      setProfile(response.data);
      setEditData({
        name: response.data.user.name,
        surname: response.data.user.surname,
        email: response.data.user.email || '',
        roles: getAllRoles(response.data.user),
        approved: response.data.user.approved
      });

      // Load athlete-specific data if user is an athlete
      if (isAthlete(response.data.user)) {
        setAthleteData({
          birth_date: response.data.user.birth_date || '',
          fiscal_code: response.data.user.fiscal_code || '',
          athlete_class: response.data.user.athlete_class || '',
          asa_reference: response.data.user.asa_reference || '',
          maestro_id: response.data.user.maestro_id || '',
          phone: response.data.user.phone || '',
          address: response.data.user.address || '',
          city: response.data.user.city || '',
          province: response.data.user.province || '',
          postal_code: response.data.user.postal_code || '',
          notes: response.data.user.notes || ''
        });
        
        // Load credits summary for athletes
        fetchCreditsSummary(response.data.user.id);
        
        // Load maestros list for dropdown
        fetchMaestros();
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Only show error toast if it's not a network/timeout error that might resolve
      if (error.response?.status !== 401) {
        toast.error('Errore nel caricamento del profilo utente');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch maestros list for dropdown
  const fetchMaestros = async () => {
    try {
      setLoadingMaestros(true);
      const response = await axios.get(`${API}/maestros`);
      const list = Array.isArray(response.data) ? [...response.data] : [];
      // Sort alphabetically by surname (then by name as tie-breaker), Italian locale.
      list.sort((a, b) => {
        const aSurname = (a?.surname || '').toLowerCase();
        const bSurname = (b?.surname || '').toLowerCase();
        const sCmp = aSurname.localeCompare(bSurname, 'it');
        if (sCmp !== 0) return sCmp;
        return (a?.name || '').toLowerCase().localeCompare((b?.name || '').toLowerCase(), 'it');
      });
      setMaestros(list);
    } catch (error) {
      console.error('Error fetching maestros:', error);
      setMaestros([]);
    } finally {
      setLoadingMaestros(false);
    }
  };

  // Fetch credits summary for athlete
  const fetchCreditsSummary = async (athleteId) => {
    try {
      setLoadingCredits(true);
      const response = await axios.get(`${API}/athlete/${athleteId}/credits-summary`);
      setCreditsSummary(response.data);
      
      // Fetch technical credits history
      fetchTechnicalCreditsHistory(athleteId);
    } catch (error) {
      console.error('Error fetching credits summary:', error);
      // Don't show error toast - credits might not be available yet
    } finally {
      setLoadingCredits(false);
    }
  };

  const fetchTechnicalCreditsHistory = async (athleteId) => {
    try {
      const response = await axios.get(`${API}/athlete/${athleteId}/technical-credits`);
      // Sort by date descending (most recent first)
      const sorted = (response.data || []).sort((a, b) => 
        new Date(b.assigned_at) - new Date(a.assigned_at)
      );
      setTechnicalCreditsHistory(sorted);
    } catch (error) {
      console.error('Error fetching technical credits history:', error);
      setTechnicalCreditsHistory([]);
    }
  };

  const updateUser = async () => {
    try {
      // Save basic user data
      await axios.patch(`${API}/admin/users/${userId}`, editData);
      
      // If user is an athlete and there's athlete data, also save athlete profile
      if (isAthlete(profile?.user) && athleteData) {
        try {
          const updateData = {};
          Object.entries(athleteData).forEach(([key, value]) => {
            if (value !== undefined) {
              updateData[key] = typeof value === 'string' ? (value.trim() || null) : value;
            }
          });
          if (updateData.fiscal_code) updateData.fiscal_code = updateData.fiscal_code.toUpperCase();
          if (updateData.province) updateData.province = updateData.province.toUpperCase();
          
          if (Object.keys(updateData).length > 0) {
            await axios.patch(`${API}/admin/users/${userId}/athlete-profile`, updateData);
          }
        } catch (athleteErr) {
          console.error('Error saving athlete data:', athleteErr);
          // Continue - basic data was already saved
        }
      }
      
      toast.success('Utente aggiornato con successo');
      setEditing(false);
      fetchUserProfile();
    } catch (error) {
      console.error('Error updating user:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Errore nell\'aggiornamento dell\'utente';
      toast.error(message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format date range for events
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // If same day, show only one date
    if (startDate === endDate) {
      return start.toLocaleDateString('it-IT', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric'
      });
    }
    
    // Different days - show range
    const startFormatted = start.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'long'
    });
    const endFormatted = end.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric'
    });
    
    return `${startFormatted} - ${endFormatted}`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <p className="text-center text-red-600">Errore nel caricamento del profilo</p>
          <Button onClick={onClose} className="w-full mt-4">Chiudi</Button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-6xl w-full my-4 shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={onClose}
                variant="ghost"
                size="sm"
                data-testid="close-profile-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Indietro
              </Button>
              <div>
                <h2 className="text-2xl font-bold text-gray-900" data-testid="profile-name">
                  {profile.user.name} {profile.user.surname}
                </h2>
                <p className="text-gray-600" data-testid="profile-email">{profile.user.email}</p>
                {/* Show athlete number if user is an athlete */}
                {isAthlete(profile.user) && profile.user.athlete_number && (
                  <p className="text-sm text-blue-600 font-semibold" data-testid="profile-athlete-number">
                    Numero Atleta: {profile.user.athlete_number}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Display multiple role badges */}
              <div className="flex flex-wrap gap-2">
                {getAllRoles(profile.user).map((role, index) => (
                  <Badge 
                    key={index}
                    className={
                      role === 'admin' ? 'badge-admin' : 
                      role === 'teacher' ? 'badge-teacher' : 
                      role === 'formatore' ? 'badge-formatore' : 
                      role === 'pending' ? 'badge-pending' : 'badge-atleta'
                    }
                    data-testid={`profile-role-badge-${role}`}
                  >
                    {role === 'pending' ? 'IN ATTESA' : 
                     role === 'formatore' ? 'FORMATORE' : 
                     role.toUpperCase()}
                  </Badge>
                ))}
              </div>
              
              {profile.user.approved ? (
                <Badge variant="default" className="bg-green-500" data-testid="profile-approved-badge">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Approvato
                </Badge>
              ) : (
                <Badge variant="secondary" data-testid="profile-pending-badge">
                  <Clock className="w-3 h-3 mr-1" />
                  In attesa
                </Badge>
              )}
              
              {/* Hide edit button for athletes viewing their own profile */}
              {!isOwnProfile && (
                <>
                <Button
                  onClick={() => setShowViewAsUser(true)}
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="view-as-user-button"
                  title="Visualizza la dashboard come la vede questo utente"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Vista Utente
                </Button>
                <Button
                  onClick={() => setEditing(!editing)}
                  variant={editing ? "destructive" : "outline"}
                  size="sm"
                  data-testid="edit-profile-button"
                >
                  {editing ? <X className="w-4 h-4 mr-1" /> : <Edit className="w-4 h-4 mr-1" />}
                  {editing ? 'Annulla' : 'Modifica'}
                </Button>
                </>
              )}
            </div>
          </div>

          {/* Edit Form - Only show for admin editing other users */}
          {editing && !isOwnProfile && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg" data-testid="edit-user-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="edit_name">Nome</Label>
                  <Input
                    id="edit_name"
                    value={editData.name}
                    onChange={(e) => setEditData({...editData, name: e.target.value})}
                    data-testid="edit-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_surname">Cognome</Label>
                  <Input
                    id="edit_surname"
                    value={editData.surname}
                    onChange={(e) => setEditData({...editData, surname: e.target.value})}
                    data-testid="edit-surname-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_email">Email</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData({...editData, email: e.target.value})}
                    data-testid="edit-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_approved">Stato</Label>
                  <Select value={editData.approved.toString()} onValueChange={(value) => setEditData({...editData, approved: value === 'true'})} data-testid="edit-approved-select">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Approvato</SelectItem>
                      <SelectItem value="false">In Attesa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Ruoli con Checkbox multipli */}
              <div className="mb-4">
                <Label className="mb-2 block">Ruoli (seleziona uno o più)</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 border rounded-lg bg-white">
                  {[
                    { value: 'atleta', label: 'Atleta', color: 'green' },
                    { value: 'teacher', label: 'Teacher', color: 'blue' },
                    { value: 'formatore', label: 'Formatore', color: 'indigo' },
                    { value: 'maestro', label: 'Maestro', color: 'orange' },
                    { value: 'contabile', label: 'Contabile', color: 'teal' },
                    { value: 'admin', label: 'Admin', color: 'purple' }
                  ].map(role => (
                    <label key={role.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editData.roles.includes(role.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditData({...editData, roles: [...editData.roles, role.value]});
                          } else {
                            setEditData({...editData, roles: editData.roles.filter(r => r !== role.value)});
                          }
                        }}
                        className={`w-4 h-4 rounded border-gray-300 text-${role.color}-600 focus:ring-${role.color}-500`}
                        data-testid={`edit-role-${role.value}`}
                      />
                      <span className={`text-sm font-medium text-${role.color}-700`}>{role.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Ruoli attuali: {editData.roles.length > 0 ? editData.roles.join(', ') : 'Nessuno'}
                </p>
              </div>
              
              <Button
                onClick={updateUser}
                className="mt-2 bg-green-500 hover:bg-green-600"
                data-testid="save-user-changes"
                disabled={editData.roles.length === 0}
              >
                <Save className="w-4 h-4 mr-1" />
                Salva Modifiche
              </Button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <Tabs defaultValue={isOwnProfile && isAthlete(profile.user) ? "athlete-profile" : "overview"} data-testid="profile-tabs">
            <TabsList className={`grid w-full ${isAthlete(profile.user) && isMaestro(profile.user) ? 'grid-cols-6' : isAthlete(profile.user) ? 'grid-cols-5' : isMaestro(profile.user) ? 'grid-cols-5' : 'grid-cols-4'}`}>
              <TabsTrigger value="overview" data-testid="overview-tab">Panoramica</TabsTrigger>
              {isAthlete(profile.user) && (
                <TabsTrigger value="athlete-profile" data-testid="athlete-profile-tab">Profilo Atleta</TabsTrigger>
              )}
              {isMaestro(profile.user) && (
                <TabsTrigger value="maestro-profile" data-testid="maestro-profile-tab">Profilo Maestro</TabsTrigger>
              )}
              <TabsTrigger value="events" data-testid="events-tab">Eventi</TabsTrigger>
              <TabsTrigger value="analyses" data-testid="analyses-tab">Match Analysis</TabsTrigger>
              <TabsTrigger value="certificates" data-testid="certificates-tab">Certificati</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" data-testid="overview-content">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Calendar className="w-5 h-5" />
                      <span>Eventi</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-600">{profile.events.length}</div>
                    <p className="text-gray-600">Eventi partecipati</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <BookOpen className="w-5 h-5" />
                      <span>Analysis</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-600">
                      {profile.match_analyses.length + profile.detailed_analyses.length}
                    </div>
                    <p className="text-gray-600">Analysis ricevute</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Award className="w-5 h-5" />
                      <span>Certificati</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-600">{profile.certificates.length}</div>
                    <p className="text-gray-600">Certificati ottenuti</p>
                  </CardContent>
                </Card>
              </div>

              {/* Full Profile Data Card */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <User className="w-5 h-5" />
                    <span>Dati Profilo Completi</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div data-testid="profile-field-email">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                      <p className="font-medium">{profile.user.email || '-'}</p>
                    </div>
                    {profile.user.phone && (
                      <div data-testid="profile-field-phone">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Telefono</p>
                        <p className="font-medium">{profile.user.phone}</p>
                      </div>
                    )}
                    {profile.user.address && (
                      <div data-testid="profile-field-address">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Indirizzo</p>
                        <p className="font-medium">{profile.user.address}</p>
                      </div>
                    )}
                    {profile.user.region && (
                      <div data-testid="profile-field-region">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Regione</p>
                        <p className="font-medium">{profile.user.region}</p>
                      </div>
                    )}
                    {profile.user.birth_date && (
                      <div data-testid="profile-field-birth-date">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Data di Nascita</p>
                        <p className="font-medium">{profile.user.birth_date}</p>
                      </div>
                    )}
                    {profile.user.fiscal_code && (
                      <div data-testid="profile-field-fiscal-code">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Codice Fiscale</p>
                        <p className="font-mono font-medium">{profile.user.fiscal_code}</p>
                      </div>
                    )}
                    {profile.user.athlete_class && (
                      <div data-testid="profile-field-class">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Classe</p>
                        <p className="font-medium">{profile.user.athlete_class}</p>
                      </div>
                    )}
                    {profile.user.asa_reference && (
                      <div data-testid="profile-field-asa">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">ASA di Riferimento</p>
                        <p className="font-medium">{profile.user.asa_reference}</p>
                      </div>
                    )}
                    {profile.user.disciplines && profile.user.disciplines.length > 0 && (
                      <div className="md:col-span-2" data-testid="profile-field-disciplines">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Discipline</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {profile.user.disciplines.map((d, i) => (
                            <span key={i} className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">{d}</span>
                          ))}
                          {profile.user.disciplines_other && (
                            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">{profile.user.disciplines_other}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Maestro School Data */}
                  {isMaestro(profile.user) && (
                    <div className="mt-6 pt-4 border-t" data-testid="profile-school-data">
                      <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                        <Award className="w-4 h-4" />
                        Dati Scuola
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div data-testid="profile-field-school-name">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Nome Scuola</p>
                          <p className="font-medium">{profile.user.school_name || <span className="text-red-400 text-sm">Non compilato</span>}</p>
                        </div>
                        <div data-testid="profile-field-school-address">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Indirizzo Scuola</p>
                          <p className="font-medium">{profile.user.school_address || <span className="text-red-400 text-sm">Non compilato</span>}</p>
                        </div>
                        <div data-testid="profile-field-school-phone">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Telefono Scuola</p>
                          <p className="font-medium">{profile.user.school_phone || <span className="text-red-400 text-sm">Non compilato</span>}</p>
                        </div>
                        <div data-testid="profile-field-school-email">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Email Scuola</p>
                          <p className="font-medium">{profile.user.school_email || <span className="text-red-400 text-sm">Non compilato</span>}</p>
                        </div>
                        <div data-testid="profile-field-school-vat">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">P.IVA / CF Scuola</p>
                          <p className="font-mono font-medium">{profile.user.school_vat || <span className="text-red-400 text-sm">Non compilato</span>}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Maestro Profile Tab */}
            {isMaestro(profile.user) && (
              <TabsContent value="maestro-profile" data-testid="maestro-profile-content">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Award className="w-5 h-5" />
                      <span>Dati Scuola del Maestro</span>
                    </CardTitle>
                    <CardDescription>Dati della scuola di danza associata a questo maestro</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Nome della Scuola</p>
                          <p className="font-semibold text-lg">{profile.user.school_name || <span className="text-red-400">Non compilato</span>}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Indirizzo della Scuola</p>
                          <p className="font-medium">{profile.user.school_address || <span className="text-red-400">Non compilato</span>}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">P.IVA / CF della Scuola</p>
                          <p className="font-mono font-semibold text-lg">{profile.user.school_vat || <span className="text-red-400">Non compilato</span>}</p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Telefono di Contatto</p>
                          <p className="font-medium">{profile.user.school_phone || <span className="text-red-400">Non compilato</span>}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Email di Contatto</p>
                          <p className="font-medium">{profile.user.school_email || <span className="text-red-400">Non compilato</span>}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Associated Athletes */}
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <User className="w-5 h-5" />
                      <span>Atleti Collegati</span>
                      {profile.associated_athletes && profile.associated_athletes.length > 0 && (
                        <Badge className="bg-purple-100 text-purple-800">{profile.associated_athletes.length}</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>Atleti associati a questo maestro</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(!profile.associated_athletes || profile.associated_athletes.length === 0) ? (
                      <p className="text-gray-500 text-center py-4">Nessun atleta collegato</p>
                    ) : (
                      <div className="space-y-2" data-testid="associated-athletes-list">
                        {profile.associated_athletes.map((athlete, idx) => (
                          <div key={athlete.id || idx} className="flex items-center justify-between border rounded-lg p-3 hover:bg-gray-50">
                            <div>
                              <p className="font-medium">{athlete.name} {athlete.surname}</p>
                              <p className="text-sm text-gray-500">{athlete.email}</p>
                              {athlete.phone && <p className="text-xs text-gray-400">Tel: {athlete.phone}</p>}
                              {athlete.region && <span className="text-xs text-gray-400">{athlete.region}</span>}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {athlete.disciplines && athlete.disciplines.map((d, i) => (
                                <span key={i} className="inline-block px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">{d}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
            {isAthlete(profile.user) && (
              <TabsContent value="athlete-profile" data-testid="athlete-profile-content">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center space-x-2">
                        <User className="w-5 h-5" />
                        <span>Dati Atleta</span>
                      </CardTitle>
                      {/* Only show edit button for admin viewing athlete profile */}
                      {!isOwnProfile && (
                        <Button
                          onClick={() => setIsEditingAthleteProfile(!isEditingAthleteProfile)}
                          variant="outline"
                          size="sm"
                          data-testid="edit-athlete-profile-button"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          {isEditingAthleteProfile ? 'Annulla' : 'Modifica'}
                        </Button>
                      )}
                    </div>
                    <CardDescription>
                      {isOwnProfile 
                        ? "Compila i tuoi dati personali. Questi dati sono obbligatori e servono per identificarti in modo univoco."
                        : "Informazioni specifiche per gli atleti per evitare problemi di omonimia"
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Always show edit mode for athletes viewing their own profile */}
                    {(!isEditingAthleteProfile && !isOwnProfile) ? (
                      /* View Mode */
                      <div className="space-y-6">
                        {/* Maestro Info - Always visible at top */}
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-orange-700">Maestro di Riferimento</Label>
                              <p className="text-lg font-semibold text-orange-900" data-testid="maestro-display">
                                {profile.user.maestro_id ? 
                                  (() => {
                                    const maestro = maestros.find(m => m.id === profile.user.maestro_id);
                                    return maestro ? `${maestro.name} ${maestro.surname}` : (loadingMaestros ? 'Caricamento...' : 'Maestro non trovato');
                                  })()
                                  : 'Nessun maestro assegnato'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Numero Atleta</Label>
                            <p className="text-lg font-bold text-blue-600 mt-1" data-testid="athlete-number-display">
                              {profile.user.athlete_number || 'Non assegnato'}
                            </p>
                          </div>
                          
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Data di Nascita</Label>
                            <p className="text-base mt-1" data-testid="birth-date-display">
                              {profile.user.birth_date || 'Non inserita'}
                            </p>
                          </div>
                          
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Codice Fiscale</Label>
                            <p className="text-base font-mono mt-1" data-testid="fiscal-code-display">
                              {profile.user.fiscal_code || 'Non inserito'}
                            </p>
                          </div>
                          
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Classe di Appartenenza</Label>
                            <p className="text-base mt-1" data-testid="athlete-class-display">
                              {profile.user.athlete_class || 'Non assegnata'}
                            </p>
                          </div>
                          
                          <div>
                            <Label className="text-sm font-medium text-gray-700">ASA di Riferimento</Label>
                            <p className="text-base mt-1" data-testid="asa-reference-display">
                              {profile.user.asa_reference || 'Non inserita'}
                            </p>
                          </div>

                          <div>
                            <Label className="text-sm font-medium text-gray-700">Telefono</Label>
                            <p className="text-base mt-1" data-testid="phone-display">
                              {profile.user.phone || 'Non inserito'}
                            </p>
                          </div>
                        </div>

                        {/* Address Info */}
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-semibold text-gray-800 mb-3">Indirizzo</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="lg:col-span-2">
                              <Label className="text-sm font-medium text-gray-700">Via/Piazza</Label>
                              <p className="text-base mt-1" data-testid="address-display">
                                {profile.user.address || 'Non inserito'}
                              </p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Città</Label>
                              <p className="text-base mt-1" data-testid="city-display">
                                {profile.user.city || 'Non inserita'}
                              </p>
                            </div>
                            <div className="flex gap-4">
                              <div>
                                <Label className="text-sm font-medium text-gray-700">Prov.</Label>
                                <p className="text-base mt-1" data-testid="province-display">
                                  {profile.user.province || '-'}
                                </p>
                              </div>
                              <div>
                                <Label className="text-sm font-medium text-gray-700">CAP</Label>
                                <p className="text-base mt-1" data-testid="postal-code-display">
                                  {profile.user.postal_code || '-'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Region Display */}
                        {profile.user.region && (
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Regione</Label>
                            <p className="text-base mt-1" data-testid="region-display">
                              {profile.user.region}
                            </p>
                          </div>
                        )}

                        {/* Admin Notes */}
                        {profile.user.notes && (
                          <div className="border-t pt-4">
                            <h4 className="text-sm font-semibold text-gray-800 mb-2">Note Admin</h4>
                            <p className="text-sm text-gray-600 bg-yellow-50 p-3 rounded border border-yellow-200">
                              {profile.user.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Edit Mode */
                      <div className="space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="birth-date">Data di Nascita (DD/MM/YYYY) *</Label>
                            <Input
                              id="birth-date"
                              type="text"
                              placeholder="01/01/1990"
                              value={athleteData.birth_date}
                              onChange={(e) => setAthleteData({...athleteData, birth_date: e.target.value})}
                              data-testid="birth-date-input"
                            />
                            <p className="text-xs text-gray-500 mt-1">Formato: giorno/mese/anno</p>
                          </div>
                          
                          <div>
                            <Label htmlFor="fiscal-code">Codice Fiscale *</Label>
                            <Input
                              id="fiscal-code"
                              type="text"
                              placeholder="RSSMRA90A01H501A"
                              value={athleteData.fiscal_code}
                              onChange={(e) => setAthleteData({...athleteData, fiscal_code: e.target.value ? e.target.value.toUpperCase() : ''})}
                              maxLength={16}
                              style={{ textTransform: 'uppercase' }}
                              data-testid="fiscal-code-input"
                            />
                            <p className="text-xs text-gray-500 mt-1">16 caratteri, lettere automaticamente maiuscole</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="athlete-class">Classe di Appartenenza *</Label>
                            <Select
                              value={athleteData.athlete_class}
                              onValueChange={(value) => setAthleteData({...athleteData, athlete_class: value})}
                              data-testid="athlete-class-select"
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Seleziona classe" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="AS">AS</SelectItem>
                                <SelectItem value="A">A</SelectItem>
                                <SelectItem value="B">B</SelectItem>
                                <SelectItem value="C">C</SelectItem>
                                <SelectItem value="D">D</SelectItem>
                                <SelectItem value="E">E</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label htmlFor="asa-reference">ASA di Riferimento</Label>
                            <Input
                              id="asa-reference"
                              type="text"
                              placeholder="Inserisci ASA di riferimento"
                              value={athleteData.asa_reference}
                              onChange={(e) => setAthleteData({...athleteData, asa_reference: e.target.value})}
                              data-testid="asa-reference-input"
                            />
                          </div>
                        </div>

                        {/* Maestro selection */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="maestro">Maestro di Riferimento</Label>
                            <Select
                              value={athleteData.maestro_id || "none"}
                              onValueChange={(value) => setAthleteData({...athleteData, maestro_id: value === "none" ? "" : value})}
                              disabled={loadingMaestros}
                              data-testid="maestro-select"
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={loadingMaestros ? "Caricamento..." : "Seleziona maestro"} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Nessun maestro</SelectItem>
                                {maestros.length === 0 && !loadingMaestros && (
                                  <SelectItem value="none" disabled>Nessun maestro disponibile</SelectItem>
                                )}
                                {maestros.map((maestro) => (
                                  <SelectItem key={maestro.id} value={maestro.id}>
                                    {maestro.name} {maestro.surname}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {maestros.length === 0 && !loadingMaestros && (
                              <p className="text-xs text-orange-600 mt-1">
                                Nessun maestro registrato nel sistema
                              </p>
                            )}
                            {maestros.length > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                Il maestro potrà gestire le tue iscrizioni agli eventi ({maestros.length} disponibili)
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Contact and Address - visible to all */}
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-semibold text-gray-800 mb-3">Contatti e Indirizzo</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="phone">Telefono *</Label>
                              <Input
                                id="phone"
                                type="tel"
                                placeholder="+39 333 1234567"
                                value={athleteData.phone}
                                onChange={(e) => setAthleteData({...athleteData, phone: e.target.value})}
                                data-testid="phone-input"
                              />
                            </div>
                            
                            <div>
                              <Label htmlFor="address">Via/Piazza *</Label>
                              <Input
                                id="address"
                                type="text"
                                placeholder="Via Roma, 1"
                                value={athleteData.address}
                                onChange={(e) => setAthleteData({...athleteData, address: e.target.value})}
                                data-testid="address-input"
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                            <div className="col-span-2">
                              <Label htmlFor="city">Città</Label>
                              <Input
                                id="city"
                                type="text"
                                placeholder="Milano"
                                value={athleteData.city}
                                onChange={(e) => setAthleteData({...athleteData, city: e.target.value})}
                                data-testid="city-input"
                              />
                            </div>
                            
                            <div>
                              <Label htmlFor="province">Provincia</Label>
                              <Input
                                id="province"
                                type="text"
                                placeholder="MI"
                                maxLength={2}
                                value={athleteData.province}
                                onChange={(e) => setAthleteData({...athleteData, province: e.target.value.toUpperCase()})}
                                style={{ textTransform: 'uppercase' }}
                                data-testid="province-input"
                              />
                            </div>
                            
                            <div>
                              <Label htmlFor="postal-code">CAP</Label>
                              <Input
                                id="postal-code"
                                type="text"
                                placeholder="20100"
                                maxLength={5}
                                value={athleteData.postal_code}
                                onChange={(e) => setAthleteData({...athleteData, postal_code: e.target.value.replace(/\D/g, '')})}
                                data-testid="postal-code-input"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Region */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="region">Regione *</Label>
                            <Select
                              value={athleteData.region || "none"}
                              onValueChange={(value) => setAthleteData({...athleteData, region: value === "none" ? "" : value})}
                              data-testid="region-select"
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Seleziona regione" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Seleziona regione</SelectItem>
                                <SelectItem value="Abruzzo">Abruzzo</SelectItem>
                                <SelectItem value="Basilicata">Basilicata</SelectItem>
                                <SelectItem value="Calabria">Calabria</SelectItem>
                                <SelectItem value="Campania">Campania</SelectItem>
                                <SelectItem value="Emilia-Romagna">Emilia-Romagna</SelectItem>
                                <SelectItem value="Friuli-Venezia Giulia">Friuli-Venezia Giulia</SelectItem>
                                <SelectItem value="Lazio">Lazio</SelectItem>
                                <SelectItem value="Liguria">Liguria</SelectItem>
                                <SelectItem value="Lombardia">Lombardia</SelectItem>
                                <SelectItem value="Marche">Marche</SelectItem>
                                <SelectItem value="Molise">Molise</SelectItem>
                                <SelectItem value="Piemonte">Piemonte</SelectItem>
                                <SelectItem value="Puglia">Puglia</SelectItem>
                                <SelectItem value="Sardegna">Sardegna</SelectItem>
                                <SelectItem value="Sicilia">Sicilia</SelectItem>
                                <SelectItem value="Toscana">Toscana</SelectItem>
                                <SelectItem value="Trentino-Alto Adige">Trentino-Alto Adige</SelectItem>
                                <SelectItem value="Umbria">Umbria</SelectItem>
                                <SelectItem value="Valle d'Aosta">Valle d'Aosta</SelectItem>
                                <SelectItem value="Veneto">Veneto</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Additional fields for admin only */}
                        {!isOwnProfile && (
                          <>
                            <div className="border-t pt-4">
                              <h4 className="text-sm font-semibold text-gray-800 mb-3">Note Admin</h4>
                              <textarea
                                id="notes"
                                className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Note interne visibili solo agli admin..."
                                value={athleteData.notes}
                                onChange={(e) => setAthleteData({...athleteData, notes: e.target.value})}
                                data-testid="notes-input"
                              />
                            </div>
                          </>
                        )}
                        
                        <div className="flex space-x-3 pt-4">
                          {/* Hide cancel button for athletes viewing their own profile */}
                          {!isOwnProfile && (
                            <Button
                              onClick={() => setIsEditingAthleteProfile(false)}
                              variant="outline"
                              className="flex-1"
                            >
                              Annulla
                            </Button>
                          )}
                          <Button
                            onClick={updateAthleteProfile}
                            className={`${isOwnProfile ? 'w-full' : 'flex-1'} bg-blue-500 hover:bg-blue-600`}
                            data-testid="save-athlete-profile-button"
                          >
                            Salva Profilo
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Credits Section */}
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Award className="w-5 h-5" />
                      <span>Crediti Anno Accademico</span>
                    </CardTitle>
                    <CardDescription>
                      Crediti formativi e tecnici dell'atleta
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingCredits ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                      </div>
                    ) : creditsSummary && creditsSummary.academic_years && creditsSummary.academic_years.length > 0 ? (
                      <div className="space-y-6">
                        {creditsSummary.academic_years.map((yearData) => (
                          <div key={yearData.academic_year} className="border rounded-lg p-4 bg-gray-50">
                            <h3 className="font-bold text-lg text-gray-800 mb-4 border-b pb-2">
                              Anno Accademico {yearData.academic_year}
                              {yearData.academic_year === creditsSummary.current_academic_year && (
                                <Badge className="ml-2 bg-blue-500">Corrente</Badge>
                              )}
                            </h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              {/* Crediti Formativi */}
                              <div className="bg-white rounded-lg p-4 border border-blue-200">
                                <h4 className="font-semibold text-blue-700 mb-2 flex items-center">
                                  <BookOpen className="w-4 h-4 mr-2" />
                                  CREDITI FORMATIVI
                                </h4>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-3xl font-bold text-blue-600">{yearData.formative_credits}</p>
                                    <p className="text-sm text-gray-500">da {yearData.events_count} event{yearData.events_count === 1 ? 'o' : 'i'}</p>
                                  </div>
                                  {yearData.formative_grade && (
                                    <Badge className={
                                      yearData.formative_grade === 'OTTIMO' ? 'bg-green-500' :
                                      yearData.formative_grade === 'BUONO' ? 'bg-blue-500' :
                                      'bg-yellow-500'
                                    }>
                                      {yearData.formative_grade}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              
                              {/* Crediti Tecnici */}
                              <div className="bg-white rounded-lg p-4 border border-purple-200">
                                <h4 className="font-semibold text-purple-700 mb-2 flex items-center">
                                  <Star className="w-4 h-4 mr-2" />
                                  CREDITI TECNICI
                                </h4>
                                <div className="flex items-center justify-between mb-3">
                                  <div>
                                    <p className="text-3xl font-bold text-purple-600">{yearData.technical_credits}</p>
                                    <p className="text-sm text-gray-500">assegnati dall'admin</p>
                                  </div>
                                  {yearData.technical_grade && (
                                    <Badge className={
                                      yearData.technical_grade === 'OTTIMO' ? 'bg-green-500' :
                                      yearData.technical_grade === 'BUONO' ? 'bg-blue-500' :
                                      'bg-yellow-500'
                                    }>
                                      {yearData.technical_grade}
                                    </Badge>
                                  )}
                                </div>
                                
                                {/* Storico Assegnazioni */}
                                {technicalCreditsHistory.filter(c => c.academic_year === yearData.academic_year).length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-purple-100">
                                    <p className="text-xs font-medium text-purple-600 mb-2">Storico Assegnazioni:</p>
                                    <div className="space-y-2 max-h-32 overflow-y-auto">
                                      {technicalCreditsHistory
                                        .filter(c => c.academic_year === yearData.academic_year)
                                        .map((credit, idx) => (
                                          <div key={credit.id || idx} className="flex items-center justify-between text-xs bg-purple-50 p-2 rounded">
                                            <div className="flex items-center space-x-2">
                                              <span className="font-semibold text-purple-700">+{credit.credits}</span>
                                              <span className="text-gray-500">
                                                {new Date(credit.assigned_at).toLocaleDateString('it-IT', {
                                                  day: '2-digit',
                                                  month: '2-digit',
                                                  year: 'numeric'
                                                })}
                                              </span>
                                              <span className="text-gray-400">da {credit.assigned_by_name}</span>
                                            </div>
                                            {credit.notes && (
                                              <button
                                                onClick={() => setSelectedCreditNote(credit)}
                                                className="text-purple-500 hover:text-purple-700 p-1"
                                                title="Visualizza note"
                                              >
                                                <Info className="w-4 h-4" />
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Voto Finale */}
                            <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                              <h4 className="font-semibold text-red-700 mb-2 flex items-center">
                                <Award className="w-4 h-4 mr-2" />
                                VOTO FINALE ANNO ACCADEMICO {yearData.academic_year}
                              </h4>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-4xl font-bold text-red-600">{yearData.total_credits}</p>
                                  <p className="text-sm text-gray-500">crediti totali</p>
                                </div>
                                <Badge className={`text-lg px-4 py-2 ${
                                  yearData.final_grade === 'ECCELLENTE' ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900' :
                                  yearData.final_grade === 'OTTIMO' ? 'bg-green-500' :
                                  yearData.final_grade === 'BUONO' ? 'bg-blue-500' :
                                  yearData.final_grade === 'PIENAMENTE SUFFICIENTE' ? 'bg-teal-500' :
                                  yearData.final_grade === 'SUFFICIENTE' ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}>
                                  {yearData.final_grade}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Nessun credito registrato per questo atleta</p>
                        <p className="text-sm mt-1">I crediti formativi vengono assegnati automaticamente con la partecipazione agli eventi</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            <TabsContent value="events" data-testid="events-content">
              <Card>
                <CardHeader>
                  <CardTitle>Eventi Partecipati</CardTitle>
                  <CardDescription>
                    Lista degli eventi ai quali l'utente si è iscritto
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {profile.events.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Nessun evento partecipato</p>
                  ) : (
                    <div className="space-y-4">
                      {profile.events.map((event) => (
                        <div key={event.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                          <h3 className="font-semibold">{event.title}</h3>
                          <p className="text-gray-600 text-sm mb-2">{event.description}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                            <span>{formatDateRange(event.start_date, event.end_date)}</span>
                            <Badge variant="secondary">{event.type}</Badge>
                          </div>
                          {event.location && (
                            <div className="text-sm text-gray-600 mb-1">📍 {event.location}</div>
                          )}
                          {event.teacher_names && event.teacher_names.length > 0 && (
                            <div className="text-sm text-gray-600">
                              Insegnanti: {event.teacher_names.join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="analyses" data-testid="analyses-content">
              <div className="space-y-6">
                {/* Match Analysis Base */}
                <Card>
                  <CardHeader>
                    <CardTitle>Match Analysis Standard</CardTitle>
                    <CardDescription>Feedback testuali ricevuti dagli insegnanti</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {profile.match_analyses.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">Nessuna match analysis standard</p>
                    ) : (
                      <div className="space-y-4">
                        {profile.match_analyses.map((analysis) => (
                          <div key={analysis.id} className="border rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-semibold">{analysis.event_title}</h3>
                              <span className="text-sm text-gray-500">{formatDate(analysis.created_at)}</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">Insegnante: {analysis.teacher_name}</p>
                            <div className="bg-gray-50 rounded p-3">
                              <p className="text-gray-700 whitespace-pre-wrap">{analysis.content}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Match Analysis Dettagliate */}
                <Card>
                  <CardHeader>
                    <CardTitle>Match Analysis Dettagliate</CardTitle>
                    <CardDescription>Analisi con punteggi dettagliati per ogni aspetto</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {profile.detailed_analyses.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">Nessuna match analysis dettagliata</p>
                    ) : (
                      <div className="space-y-4">
                        {profile.detailed_analyses.map((analysis) => (
                          <div key={analysis.id} className="border rounded-lg p-4">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h3 className="font-semibold text-lg">{analysis.event_title}</h3>
                                <p className="text-sm text-gray-600 mb-1">Insegnante: {analysis.teacher_name}</p>
                                <span className="text-sm text-gray-500">{formatDate(analysis.created_at)}</span>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-green-600">{analysis.total_score}/10</div>
                                <div className="text-sm text-gray-500">Media</div>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
                              {Object.entries(analysis.scores || {}).map(([key, value]) => (
                                <div key={key} className="text-center p-2 bg-blue-50 rounded border border-blue-200">
                                  <div className="text-xs text-blue-600 mb-1 font-medium">
                                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </div>
                                  <div className="text-lg font-bold text-blue-800">{value}/10</div>
                                </div>
                              ))}
                            </div>
                            
                            {analysis.notes && (
                              <div className="bg-green-50 rounded p-3 border border-green-200">
                                <h4 className="font-medium text-green-800 mb-2">Note dell'Insegnante:</h4>
                                <p className="text-green-700 whitespace-pre-wrap">{analysis.notes}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="certificates" data-testid="certificates-content">
              <Card>
                <CardHeader>
                  <CardTitle>Certificati</CardTitle>
                  <CardDescription>Certificati e attestati rilasciati all'utente</CardDescription>
                </CardHeader>
                <CardContent>
                  {profile.certificates.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Nessun certificato rilasciato</p>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      {profile.certificates.map((cert) => (
                        <div key={cert.id} className="border rounded-lg p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                          <div className="flex items-start space-x-3">
                            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                              <Award className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-blue-900">{cert.title}</h3>
                              {cert.description && (
                                <p className="text-blue-700 text-sm mt-1">{cert.description}</p>
                              )}
                              <p className="text-blue-600 text-xs mt-2">
                                Rilasciato il {formatDate(cert.issued_at)}
                              </p>
                              {cert.pdf_filename && (
                                <Button
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('token');
                                      const response = await axios.get(`${API}/certificates/${cert.id}/download`, {
                                        responseType: 'blob',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.setAttribute('download', `${cert.title.replace(/ /g, '_')}.pdf`);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      window.URL.revokeObjectURL(url);
                                    } catch (error) {
                                      const errorMessage = error.response?.data?.detail || 'Errore nel download del certificato';
                                      toast.error(errorMessage + '. Contatta l\'admin per rigenerare il PDF.');
                                    }
                                  }}
                                  size="sm"
                                  className="mt-2 bg-blue-600 hover:bg-blue-700 text-white"
                                  data-testid={`download-cert-profile-${cert.id}`}
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  Scarica PDF
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* Modal per note credito tecnico */}
      {selectedCreditNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-purple-700 flex items-center">
                <Info className="w-5 h-5 mr-2" />
                Note Assegnazione Crediti
              </h3>
              <button
                onClick={() => setSelectedCreditNote(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Crediti assegnati:</span> {selectedCreditNote.credits}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Data:</span> {new Date(selectedCreditNote.assigned_at).toLocaleDateString('it-IT', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Assegnato da:</span> {selectedCreditNote.assigned_by_name}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Anno Accademico:</span> {selectedCreditNote.academic_year}
                </p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Note:</p>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedCreditNote.notes || 'Nessuna nota'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                onClick={() => setSelectedCreditNote(null)}
              >
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* View As User Modal */}
    {showViewAsUser && profile && (
      <ViewAsUser
        userId={userId}
        userName={`${profile.user.name} ${profile.user.surname}`}
        onClose={() => setShowViewAsUser(false)}
      />
    )}
    </>
  );
};

export default UserProfile;